import React, { useState, useEffect } from 'react';
import { useAuth } from '../../../services/contexts/AuthContext';
import { 
  Heart, 
  Activity, 
  TrendingUp, 
  Calendar, 
  Clock, 
  AlertTriangle,
  CheckCircle,
  Award,
  User,
  FileText,
  BarChart3,
  Shield,
  Zap,
  Target,
  Star,
  Clock3,
  TrendingDown,
  AlertCircle
} from 'lucide-react';

const TenantPatientDashboard = () => {
  const { user } = useAuth();
  const [dashboardData, setDashboardData] = useState({
    recentVitals: [],
    healthScore: 0,
    streakDays: 0,
    nextAppointment: null,
    alerts: []
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      setDashboardData({
        recentVitals: [
          {
            id: 1,
            created_at: new Date().toISOString(),
            heart_rate: 72,
            rhythm: 'Normal',
            quality_flag: 'High'
          },
          {
            id: 2,
            created_at: new Date(Date.now() - 86400000).toISOString(),
            heart_rate: 75,
            rhythm: 'Normal',
            quality_flag: 'High'
          }
        ],
        healthScore: 85,
        streakDays: 12,
        nextAppointment: new Date(Date.now() + 7 * 86400000),
        alerts: [
          { id: 1, type: 'info', message: 'Your next recording is due in 2 days' },
          { id: 2, type: 'success', message: 'Great job maintaining your health routine!' }
        ]
      });
    } catch (error) {
      console.error('Failed to load dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const StatCard = ({ title, value, icon: Icon, color = "brand-blue", change = null, status = null }) => (
    <div className={`bg-white p-6 rounded-xl shadow-sm border-l-4 border-l-${color}-500 border-r border-t border-b border-va-neutral-200`}>
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-medium text-va-neutral-600">{title}</p>
          <p className={`text-2xl font-bold mt-2 text-${color}-600`}>{value}</p>
          {change && (
            <p className={`text-sm mt-1 ${change > 0 ? 'text-va-health-600' : 'text-va-error-600'}`}>
              {change > 0 ? '+' : ''}{change}% from last week
            </p>
          )}
          {status && (
            <p className="text-xs text-va-neutral-500 mt-1">{status}</p>
          )}
        </div>
        <div className={`p-3 rounded-lg bg-${color}-100`}>
          <Icon className={`h-6 w-6 text-${color}-600`} />
        </div>
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-blue"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-va-neutral-900">My Health Dashboard</h1>
        <p className="text-va-neutral-600">Welcome back, {user?.name}. Here's your health overview.</p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <StatCard 
          title="Health Score" 
          value={`${dashboardData.healthScore}%`} 
          icon={Heart} 
          color="brand-blue"
          change={5}
        />
        <StatCard 
          title="Recording Streak" 
          value={`${dashboardData.streakDays} days`} 
          icon={TrendingUp} 
          color="brand-cyan"
          change={2}
        />
        <StatCard 
          title="Total Recordings" 
          value={dashboardData.totalRecordings || dashboardData.recentVitals.length} 
          icon={Activity} 
          color="va-health-600"
          change={0}
        />
        <StatCard 
          title="Next Appointment" 
          value={dashboardData.nextAppointment ? 'Scheduled' : 'None'} 
          icon={Calendar} 
          color="va-warning-600"
          change={null}
        />
      </div>

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Recent Vitals */}
        <div className="lg:col-span-2 bg-white rounded-xl shadow-sm p-6">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-semibold text-va-neutral-900">Recent Vital Signs</h3>
            <button className="text-sm text-brand-blue hover:text-brand-darkCyan font-medium">
              View All →
            </button>
          </div>
          
          {dashboardData.recentVitals.length === 0 ? (
            <div className="text-center py-8">
              <Activity className="h-12 w-12 text-va-neutral-400 mx-auto mb-4" />
              <p className="text-va-neutral-500">No recent recordings</p>
            </div>
          ) : (
            <div className="space-y-4">
              {dashboardData.recentVitals.map((vital) => (
                <div key={vital.id} className="border border-va-neutral-200 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center space-x-2">
                      <Calendar className="h-4 w-4 text-va-neutral-400" />
                      <span className="text-sm font-medium text-va-neutral-900">
                        {new Date(vital.created_at).toLocaleDateString()}
                      </span>
                      <span className="text-sm text-va-neutral-500">
                        at {new Date(vital.created_at).toLocaleTimeString()}
                      </span>
                    </div>
                    
                    <span className={`px-2.5 py-0.5 text-xs font-medium rounded-full ${
                      vital.quality_flag === 'High' ? 'bg-va-health-100 text-va-health-800' :
                      vital.quality_flag === 'Medium' ? 'bg-va-warning-100 text-va-warning-800' :
                      'bg-va-error-100 text-va-error-800'
                    }`}>
                      {vital.quality_flag || 'Normal'}
                    </span>
                  </div>
                  
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                    <div className="text-center">
                      <p className="text-xs text-va-neutral-600">Heart Rate</p>
                      <p className="text-lg font-bold text-va-error-600">{vital.heart_rate || 'N/A'}</p>
                    </div>
                    <div className="text-center">
                      <p className="text-xs text-va-neutral-600">Rhythm</p>
                      <p className={`text-lg font-bold ${
                        vital.rhythm && ['Irregular', 'Atrial Fibrillation'].includes(vital.rhythm) 
                          ? 'text-va-error-600' 
                          : 'text-va-health-600'
                      }`}>
                        {vital.rhythm || 'Normal'}
                      </p>
                    </div>
                    {vital.blood_pressure_systolic && (
                      <div className="text-center">
                        <p className="text-xs text-va-neutral-600">Blood Pressure</p>
                        <p className="text-lg font-bold text-brand-blue">
                          {vital.blood_pressure_systolic}/{vital.blood_pressure_diastolic}
                        </p>
                      </div>
                    )}
                  </div>
                  {vital.rhythm && ['Irregular', 'Atrial Fibrillation'].includes(vital.rhythm) && (
                    <div className="mt-3 p-2 bg-va-error-50 border border-va-error-200 rounded">
                      <div className="flex items-center space-x-2">
                        <AlertCircle className="h-4 w-4 text-va-error-600" />
                        <span className="text-sm text-va-error-800">
                          Your healthcare team has been notified about this irregular rhythm
                        </span>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Right Sidebar */}
        <div className="space-y-6">
          {/* Health Streak */}
          <div className="bg-white rounded-xl shadow-sm p-6">
            <div className="flex items-center space-x-3 mb-4">
              <div className="p-2 bg-va-primary-100 rounded-lg">
                <Award className="h-5 w-5 text-va-primary-600" />
              </div>
              <div>
                <h3 className="font-semibold text-va-neutral-900">Health Streak</h3>
                <p className="text-sm text-va-neutral-600">Keep it going!</p>
              </div>
            </div>
            <div className="text-center">
              <p className="text-3xl font-bold text-va-primary-600">{dashboardData.streakDays}</p>
              <p className="text-sm text-va-neutral-600">days in a row</p>
            </div>
            <div className="mt-4 flex items-center justify-center">
              <Award className="h-3 w-3 text-va-primary-600" />
              <span className="text-xs text-va-primary-600 ml-1">Great!</span>
            </div>
          </div>

          {/* Quick Actions */}
          <div className="bg-white rounded-xl shadow-sm p-6">
            <h3 className="font-semibold text-va-neutral-900 mb-4">Quick Actions</h3>
            <div className="space-y-3">
              <button className="w-full flex items-center space-x-3 p-3 text-left bg-va-primary-50 hover:bg-va-primary-100 rounded-lg transition-colors">
                <User className="h-5 w-5 text-va-primary-600" />
                <div>
                  <p className="font-medium text-va-neutral-900">Start Recording</p>
                  <p className="text-sm text-va-neutral-600">Record your vitals</p>
                </div>
              </button>
              <button className="w-full flex items-center space-x-3 p-3 text-left bg-va-accent-50 hover:bg-va-accent-100 rounded-lg transition-colors">
                <FileText className="h-5 w-5 text-va-accent-600" />
                <div>
                  <p className="font-medium text-va-neutral-900">View History</p>
                  <p className="text-sm text-va-neutral-600">See past recordings</p>
                </div>
              </button>
              <button className="w-full flex items-center space-x-3 p-3 text-left bg-va-health-50 hover:bg-va-health-100 rounded-lg transition-colors">
                <BarChart3 className="h-5 w-5 text-va-health-600" />
                <div>
                  <p className="font-medium text-va-neutral-900">Health Trends</p>
                  <p className="text-sm text-va-neutral-600">Track your progress</p>
                </div>
              </button>
            </div>
          </div>

          {/* Health Insights */}
          <div className="bg-white rounded-xl shadow-sm p-6">
            <h3 className="font-semibold text-va-neutral-900 mb-4">Health Insights</h3>
            <div className="space-y-4">
              <div className="flex items-center space-x-3">
                <div className="p-2 bg-va-health-100 rounded-lg">
                  <TrendingUp className="h-4 w-4 text-va-health-600" />
                </div>
                <div>
                  <p className="text-sm font-medium text-va-neutral-900">Consistent Heart Rate</p>
                  <p className="text-xs text-va-neutral-600">Your heart rate is stable</p>
                </div>
              </div>
              <div className="flex items-center space-x-3">
                <div className="p-2 bg-va-warning-100 rounded-lg">
                  <Clock3 className="h-4 w-4 text-va-warning-600" />
                </div>
                <div>
                  <p className="text-sm font-medium text-va-neutral-900">Next Recording Due</p>
                  <p className="text-xs text-va-neutral-600">In 2 days</p>
                </div>
              </div>
              <div className="flex items-center space-x-3">
                <div className="p-2 bg-va-primary-100 rounded-lg">
                  <Target className="h-4 w-4 text-va-primary-600" />
                </div>
                <div>
                  <p className="text-sm font-medium text-va-neutral-900">Goal Progress</p>
                  <p className="text-xs text-va-neutral-600">85% of monthly target</p>
                </div>
              </div>
            </div>
          </div>

          {/* Alerts */}
          {dashboardData.alerts.length > 0 && (
            <div className="bg-white rounded-xl shadow-sm p-6">
              <h3 className="font-semibold text-va-neutral-900 mb-4">Alerts</h3>
              <div className="space-y-3">
                {dashboardData.alerts.map((alert) => (
                  <div key={alert.id} className={`p-3 rounded-lg ${
                    alert.type === 'success' ? 'bg-va-health-50 border border-va-health-200' :
                    alert.type === 'warning' ? 'bg-va-warning-50 border border-va-warning-200' :
                    'bg-va-primary-50 border border-va-primary-200'
                  }`}>
                    <div className="flex items-center space-x-2">
                      {alert.type === 'success' ? (
                        <CheckCircle className="h-4 w-4 text-va-health-600" />
                      ) : alert.type === 'warning' ? (
                        <AlertTriangle className="h-4 w-4 text-va-warning-600" />
                      ) : (
                        <AlertCircle className="h-4 w-4 text-va-primary-600" />
                      )}
                      <span className="text-sm text-va-neutral-900">{alert.message}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TenantPatientDashboard;